import { Injectable } from '@angular/core';
import { ConnectionStatusService } from './connection-status.service';
import { Observable } from 'rxjs';
import { TaskList } from '../model/task-list/task-list';
import { StringHelper } from '../helpers/string-helper';
import { RequestService } from './request.service';
import { map } from 'rxjs/operators';
import { OfflineProjectStorageService } from './offline-project-storage.service';
import { CollectionHelper } from '../helpers/collection-helper';
import { OfflineStorageTimestampService } from './offline-storage-timestamp.service';
import SemaphoreHelper from '../helpers/semaphore-helper';
import * as semaphore from 'semaphore';
import { Task, TASK_TYPES } from '../model/task-list/task';
import { ExplicitlyCompletableTask } from '../model/task-list/explicitly-completable-task';
import RegisteredAction from '../model/registered-action';

@Injectable({
  providedIn: 'root',
})
export class PlacerTasksService {
  private getFromOnline: boolean = true;
  static readonly storageKeyPrefix = 'tasklist-';
  static readonly timestampCollectionKey = 'tasklists';
  static readonly offlineStorageMutex = semaphore(1);

  private expiredTaskListRemover: ExpiredTasklistRemover;

  constructor(
    private requestService: RequestService,
    private connectionStatusService: ConnectionStatusService,
    private offlineProjectStorageService: OfflineProjectStorageService,
    private offlineStorageTimestampService: OfflineStorageTimestampService
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        this.getFromOnline = online;
      });
    this.expiredTaskListRemover = new ExpiredTasklistRemover(
      this.offlineStorageTimestampService
    );
    this.expiredTaskListRemover.removeExpiredItems();
  }

  static getStorageKeyFor(employeeId: number, dateString: string) {
    if (employeeId == null) {
      throw new Error('employeeId must not be null');
    }
    if (StringHelper.isEmpty(dateString)) {
      throw new Error('dateString must not be null or empty');
    }
    return `${this.storageKeyPrefix + employeeId}-${dateString}`;
  }

  static getStorageKeyForTimestampItem(timestampItemKey: string) {
    if (StringHelper.isEmpty(timestampItemKey)) {
      throw new Error('timestampItemKey must not be null or empty');
    }
    return `${this.storageKeyPrefix}${timestampItemKey}`;
  }

  public getTaskList(
    employeeId: number,
    dateString: string
  ): Observable<TaskList> {
    if (employeeId == null) {
      throw new Error('employee id must not be null');
    }
    if (StringHelper.isEmpty(dateString)) {
      throw new Error('dateString must not be null or empty');
    }
    return this.getFromOnline
      ? this.getTaskListFromOnline(employeeId, dateString)
      : this.getTaskListFromOffline(employeeId, dateString);
  }

  private getTaskListFromOnline(
    employeeId: number,
    dateString: string
  ): Observable<TaskList> {
    const toReturn = this.requestService.get(
      `/tasklist/offlineStorage/${employeeId}/${dateString}`
    );
    toReturn.subscribe((res) => {
      if (res != null && res.body != null) {
        if (res.body.taskList != null) {
          SemaphoreHelper.use(
            PlacerTasksService.offlineStorageMutex,
            async () => {
              localStorage.setItem(
                PlacerTasksService.getStorageKeyFor(employeeId, dateString),
                JSON.stringify(res.body.taskList)
              );
              this.offlineStorageTimestampService.setTimestamps(
                PlacerTasksService.timestampCollectionKey,
                [{ key: `${employeeId}-${dateString}`, timestamp: Date.now() }]
              );
            }
          );
        }
        if (CollectionHelper.isPopulated(res.body.projects)) {
          this.offlineProjectStorageService.storeProjects(res.body.projects);
        }
      }
    });
    return toReturn.pipe(
      map((res) => {
        if (res == null || res.body == null) {
          return null;
        }
        const toReturn = <TaskList>res.body.taskList;
        toReturn.fromOnline = true;
        return toReturn;
      })
    );
  }

  private getTaskListFromOffline(
    employeeId: number,
    dateString: string
  ): Observable<TaskList> {
    const toReturn = localStorage.getItem(
      PlacerTasksService.getStorageKeyFor(employeeId, dateString)
    );
    if (toReturn != null) {
      SemaphoreHelper.use(PlacerTasksService.offlineStorageMutex, async () => {
        this.offlineStorageTimestampService.setTimestamps(
          PlacerTasksService.timestampCollectionKey,
          [{ key: `${employeeId}-${dateString}`, timestamp: Date.now() }]
        );
      });
    }
    return new Observable((subscriber) => {
      const parsed = toReturn == null ? null : <TaskList>JSON.parse(toReturn);
      if (parsed != null) {
        parsed.fromOnline = false;
      }
      subscriber.next(parsed);
    });
  }

  public async getTaskFromOffline(
    employeeId: number,
    dateString: string,
    taskId: number
  ): Promise<Task> {
    const taskListString = localStorage.getItem(
      PlacerTasksService.getStorageKeyFor(employeeId, dateString)
    );
    if (taskListString != null) {
      SemaphoreHelper.use(PlacerTasksService.offlineStorageMutex, async () => {
        this.offlineStorageTimestampService.setTimestamps(
          PlacerTasksService.timestampCollectionKey,
          [{ key: `${employeeId}-${dateString}`, timestamp: Date.now() }]
        );
      });
    } else {
      return null;
    }

    const taskList = <TaskList>JSON.parse(taskListString);
    return !CollectionHelper.isPopulated(taskList.tasks)
      ? null
      : taskList.tasks.find((t) => t.id === taskId);
  }

  public storeTask(task: Task, employeeId: number, dateString: string): void {
    if (task == null || employeeId == null || dateString == null) {
      throw new Error('None of task, employeeId or dataString may be null');
    }
    SemaphoreHelper.use(PlacerTasksService.offlineStorageMutex, async () => {
      const taskList = this.retrieveTaskList(employeeId, dateString);

      if (taskList == null) {
        return;
      }

      const oldTaskIndex = CollectionHelper.isPopulated(taskList.tasks)
        ? taskList.tasks.findIndex((t) => t.id === task.id)
        : -1;
      if (oldTaskIndex < 0) {
        return;
      }

      taskList.tasks[oldTaskIndex] = task;

      localStorage.setItem(
        PlacerTasksService.getStorageKeyFor(employeeId, dateString),
        JSON.stringify(taskList)
      );

      this.offlineStorageTimestampService.setTimestamps(
        PlacerTasksService.timestampCollectionKey,
        [{ key: `${employeeId}-${dateString}`, timestamp: Date.now() }]
      );
    });
  }

  public async storeRegisteredActions(
    taskId: number,
    employeeId: number,
    dateString: string,
    registeredActions: RegisteredAction[]
  ): Promise<void> {
    if (
      taskId == null ||
      employeeId == null ||
      dateString == null ||
      !CollectionHelper.isPopulated(registeredActions)
    ) {
      throw new Error(
        'None of taskId, employeeId, dateString or registeredActions may be null or empty'
      );
    }
    await SemaphoreHelper.use(
      PlacerTasksService.offlineStorageMutex,
      async () => {
        const taskList = this.retrieveTaskList(employeeId, dateString);

        if (taskList == null) {
          return;
        }

        const oldTaskIndex = CollectionHelper.isPopulated(taskList.tasks)
          ? taskList.tasks.findIndex((t) => t.id === taskId)
          : -1;
        if (oldTaskIndex < 0) {
          return;
        }

        const savedTask = taskList.tasks[oldTaskIndex];
        if (
          savedTask.type !== TASK_TYPES.LOAD_TASK &&
          savedTask.type !== TASK_TYPES.OTHER_TASK
        ) {
          return;
        }

        (<ExplicitlyCompletableTask>savedTask).registeredActions =
          registeredActions;

        localStorage.setItem(
          PlacerTasksService.getStorageKeyFor(employeeId, dateString),
          JSON.stringify(taskList)
        );

        this.offlineStorageTimestampService.setTimestamps(
          PlacerTasksService.timestampCollectionKey,
          [{ key: `${employeeId}-${dateString}`, timestamp: Date.now() }]
        );
      }
    );
  }

  private retrieveTaskList(employeeId: number, dateString: string): TaskList {
    const taskListString = localStorage.getItem(
      PlacerTasksService.getStorageKeyFor(employeeId, dateString)
    );
    return taskListString == null ? null : <TaskList>JSON.parse(taskListString);
  }
}

class ExpiredTasklistRemover {
  private readonly expiredItemRemovalInterval = 1800000; // 30 minutes

  constructor(
    private offlineStorageTimestampService: OfflineStorageTimestampService
  ) {}

  removeExpiredItems() {
    const expiredItems = this.offlineStorageTimestampService.getExpiredItems(
      PlacerTasksService.timestampCollectionKey
    );
    if (CollectionHelper.isPopulated(expiredItems)) {
      expiredItems.forEach((i) =>
        localStorage.removeItem(
          PlacerTasksService.getStorageKeyForTimestampItem(i.key)
        )
      );
      this.offlineStorageTimestampService.removeItems(
        PlacerTasksService.timestampCollectionKey,
        expiredItems.map((i) => i.key)
      );
    }
    setTimeout(
      () => this.removeExpiredItems(),
      this.expiredItemRemovalInterval
    );
  }
}
